import { ModifyDealWarningDialog } from '@src/containers/Blotters/Orders/Dialogs/ModifyDealWarningDialog';
import type { OTCDealData } from '@src/types/OTC';
import { getOtcDealDisplayUser } from '@src/utils/OTC/otcDealDisplayUtils';
import { useDisclosureWithContext, useUserContext } from '@talos/kyoko';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useModifyDeal } from './useModifyDeal';

/**
 * Shared "Modify Deal" menu behaviour for OTC blotters (loans, derivatives, repos).
 *
 * Opens the deal straight into the modify form when it belongs to the current user, otherwise
 * surfaces the warning dialog first. Owns the warning dialog so every blotter behaves identically.
 */
export function useModifyDealMenuAction(): {
  handleModifyDeal: (deal: OTCDealData) => void;
  modifyDealDialog: ReactNode;
} {
  const onModifyDeal = useModifyDeal();
  const { user } = useUserContext();
  const modifyDealWarningDialog = useDisclosureWithContext<OTCDealData>({});

  const handleModifyDeal = useCallback(
    (deal: OTCDealData) => {
      if (getOtcDealDisplayUser(deal) === user.Name) {
        onModifyDeal(deal);
      } else {
        modifyDealWarningDialog.open(deal);
      }
    },
    [onModifyDeal, user.Name, modifyDealWarningDialog]
  );

  const modifyDealDialog = useMemo(
    () => (
      <ModifyDealWarningDialog
        disclosure={modifyDealWarningDialog}
        handleConfirm={deal => {
          onModifyDeal(deal);
        }}
      />
    ),
    [modifyDealWarningDialog, onModifyDeal]
  );

  return { handleModifyDeal, modifyDealDialog };
}
